import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, Send, Loader2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { MessagingUpgradeDialog } from './MessagingUpgradeDialog';

export function MessageDialog({
  open,
  onOpenChange,
  tocName,
  conversation,
  messageText,
  setMessageText,
  onSendMessage,
  onDeleteMessage,
  isSending,
  userId,
  sendDisabled = false,
  showUpgradePrompt = false,
  onUpgradePromptChange,
}) {
  // Head Start (no in_app_messaging): let the admin type a draft, but intercept
  // send and show the upgrade prompt instead of calling the API.
  const handleSubmit = () => {
    if (!messageText.trim() || isSending) return;
    if (sendDisabled) {
      onUpgradePromptChange?.(true);
      return;
    }
    onSendMessage();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-slate-600" />
            Message with {tocName || 'Substitute'}
          </DialogTitle>
          <DialogDescription>
            Your conversation history with this substitute
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 min-h-[300px] max-h-[400px] pr-4">
          <div className="space-y-3">
            {conversation.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                No messages yet. Start the conversation below.
              </p>
            ) : (
              conversation.map((msg) => (
                <div 
                  key={msg.message_id}
                  className={`flex ${msg.sender_id === userId ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-lg p-3 ${
                    msg.sender_id === userId 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-slate-100 text-slate-900'
                  }`}>
                    <p className="text-sm">{msg.content}</p>
                    <div className={`flex items-center justify-between mt-1`}>
                      <p className={`text-xs ${
                        msg.sender_id === userId ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                      </p>
                      {msg.sender_id === userId && onDeleteMessage && (
                        <button 
                          onClick={() => onDeleteMessage(msg.message_id)}
                          className={`text-xs opacity-60 hover:opacity-100 ${
                            msg.sender_id === userId ? 'text-slate-400' : 'text-slate-500'
                          }`}
                          title="Delete message"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
        
        <div className="flex gap-2 pt-4 border-t">
          <Input
            placeholder="Type your message..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            disabled={isSending}
            data-testid="message-input"
          />
          <Button
            onClick={handleSubmit}
            disabled={!messageText.trim() || isSending}
            className="bg-slate-800 hover:bg-slate-700"
            data-testid="send-message-btn"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    <MessagingUpgradeDialog
      open={showUpgradePrompt}
      onOpenChange={(next) => onUpgradePromptChange?.(next)}
    />
    </>
  );
}
